import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import { Newspaper, Vote, ScrollText, UserCircle, LayoutDashboard, LogOut } from "lucide-react";

const NAV_ITEMS = [
  { to: "/posts", label: "المنشورات", icon: Newspaper, testid: "nav-posts-link" },
  { to: "/votes", label: "التصويتات", icon: Vote, testid: "nav-votes-link" },
  { to: "/bylaws", label: "النظام الداخلي", icon: ScrollText, testid: "nav-bylaws-link" },
  { to: "/account", label: "حسابي", icon: UserCircle, testid: "nav-account-link" },
];

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white" data-testid="app-loading">
      <img src={LOGO_URL} alt="نابو" className="h-20 w-20 animate-pulse rounded-2xl border border-gold/40" />
    </div>
  );
}

export default function AppShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const navItems = user.role === "admin"
    ? [...NAV_ITEMS, { to: "/admin", label: "لوحة التحكم", icon: LayoutDashboard, testid: "nav-admin-link" }]
    : NAV_ITEMS;

  return (
    <div className="min-h-screen bg-white" data-testid="app-shell">
      <header className="sticky top-0 z-40 border-b border-gold/25 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="نابو" className="h-10 w-10 rounded-xl border border-gold/40" data-testid="header-logo" />
            <span className="font-amiri text-2xl font-bold text-gold-gradient">نابو</span>
            {!user.is_active && (
              <span className="rounded-full border border-amber-500/50 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-600" data-testid="pending-activation-badge">
                بانتظار التفعيل
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-medium text-navy sm:block" data-testid="header-username">{user.name}</span>
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full border border-gold/40" referrerPolicy="no-referrer" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-navy font-amiri text-lg text-gold">
                {user.name?.[0] || "ن"}
              </span>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="rounded-full p-2 text-muted-foreground transition-[color,background-color] duration-300 hover:bg-red-50 hover:text-red-500"
              title="تسجيل الخروج"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 pb-36 pt-10">
        <Outlet />
      </main>

      <nav className="pointer-events-none fixed inset-x-0 bottom-5 z-50 flex justify-center px-4" data-testid="main-nav">
        <motion.div
          className="pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-gold/40 bg-navy/95 p-2 shadow-[0_20px_60px_rgba(10,30,60,0.35)] backdrop-blur-xl"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `relative flex shrink-0 items-center gap-2 rounded-full px-3.5 py-3 text-sm font-bold transition-[color] duration-300 sm:px-5 ${
                  isActive ? "text-navy" : "text-white/70 hover:text-gold"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="nav-pill"
                      className="absolute inset-0 rounded-full bg-gold"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  <item.icon className="relative z-10 h-5 w-5" />
                  <span className="relative z-10 hidden sm:block">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </motion.div>
      </nav>
    </div>
  );
}
